import { useState, useEffect } from 'react';
import { Eye, EyeOff, CheckCircle } from 'lucide-react';
import { useApp } from '../lib/AppContext';
import Button from '../components/Button';
import Header from '../components/Header';
import { supabase } from '../lib/supabaseClient';

export default function ResetPassword() {
  const { navigateTo, showToast } = useApp();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionReady, setSessionReady] = useState(false);
  const [showMatchError, setShowMatchError] = useState(false);

  useEffect(() => {
    const establishSession = async () => {
      const hash = window.location.hash;
      const search = window.location.search;

      // Parse tokens from BOTH hash and query string
      // Supabase can send in different formats:
      // Format 1: #/reset-password?access_token=xxx
      // Format 2: ?access_token=xxx#/reset-password
      // Format 3: #access_token=xxx (direct hash fragment)
      // Format 4: #/reset-password#access_token=xxx (double hash - Supabase bug)

      let queryString = '';

      // Parse tokens from hash - check formats in order of specificity
      if (hash) {
        const hashContent = hash.substring(1); // Remove leading #

        // Format 1: #/reset-password?access_token=xxx (standard format after redirect fix)
        if (hashContent.includes('?')) {
          const parts = hashContent.split('?');
          queryString = parts[1] || '';
          console.log('[ResetPassword] Standard format detected:', queryString);
        }
        // Format 2: #/reset-password#access_token=xxx (double hash - Supabase bug)
        else if (hashContent.includes('#')) {
          const parts = hashContent.split('#');
          if (parts.length > 1 && parts[1]) {
            queryString = parts[1];
            console.log('[ResetPassword] Double hash detected, extracted:', queryString);
          }
        }
        // Format 3: #access_token=xxx (direct hash fragment)
        else if (hashContent.includes('access_token') || hashContent.includes('error')) {
          if (hashContent.includes('&') && !hashContent.startsWith('/')) {
            queryString = hashContent;
            console.log('[ResetPassword] Direct hash fragment:', queryString);
          }
        }
      }

      // If no params in hash, check query string
      if (!queryString && search) {
        queryString = search.substring(1);
        console.log('[ResetPassword] Using query string:', queryString);
      }

      const params = new URLSearchParams(queryString);

      console.log('[ResetPassword] Parsed query string:', queryString);
      console.log('[ResetPassword] Has access_token:', params.has('access_token'));
      console.log('[ResetPassword] Has error:', params.has('error'));

      // FIRST: Check if there's an error in the URL (Supabase redirected with error)
      // This happens when clicking an already-used or expired link
      if (hash && hash.includes('error')) {
        // Before showing error, check if we have an active session
        // (User might have clicked the link twice in quick succession)
        const { data: { session: existingSession } } = await supabase.auth.getSession();

        if (existingSession && existingSession.user) {
          console.log('[ResetPassword] URL has error but valid session exists, proceeding');
          setSessionReady(true);
          // Clean up the URL
          window.history.replaceState(null, '', '#/reset-password');
          return;
        }

        // No valid session, show the error
        const errorDescription = params.get('error_description');
        const errorCode = params.get('error_code');

        // Decode the error description
        const decodedError = errorDescription ? decodeURIComponent(errorDescription.replace(/\+/g, ' ')) : null;

        // Set a unified error state for expired/invalid/already-used links
        setError('expired');
        return;
      }

      // SECOND: Check if we already have an active recovery session
      // This allows users to refresh the page or return to it
      const { data: { session: existingSession } } = await supabase.auth.getSession();

      if (existingSession && existingSession.user) {
        console.log('[ResetPassword] Existing recovery session found, reusing it');
        setSessionReady(true);

        // Clean up the URL to remove tokens (prevents confusion on refresh)
        if (window.location.hash.includes('access_token')) {
          window.history.replaceState(null, '', '#/reset-password');
        }
        return;
      }

      // Check if there's a valid access token in the URL
      if (!hash || !hash.includes('access_token')) {
        setError('expired');
        return;
      }

      // Extract tokens and establish session
      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');
      const type = params.get('type');

      console.log('[ResetPassword] Recovery tokens detected:', { hasAccess: !!accessToken, hasRefresh: !!refreshToken, type });

      if (accessToken && type === 'recovery') {
        try {
          // Exchange the recovery tokens for a session
          // NOTE: This consumes the one-time token, but the session remains valid
          const { data, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || '',
          });

          if (sessionError) {
            console.error('[ResetPassword] Session establishment error:', sessionError);

            // Set unified error state for all session establishment failures
            setError('expired');
            return;
          }

          console.log('[ResetPassword] Session established successfully:', data.session?.user?.email);
          setSessionReady(true);

          // Clean up the URL to remove the used tokens
          // This prevents the "already used" error if user refreshes the page
          window.history.replaceState(null, '', '#/reset-password');
        } catch (err) {
          console.error('[ResetPassword] Unexpected error establishing session:', err);
          setError('expired');
        }
      } else {
        setError('expired');
      }
    };

    establishSession();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password.trim()) {
      showToast('Please enter a new password');
      return;
    }

    if (password.length < 6) {
      showToast('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setShowMatchError(true);
      showToast('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password,
      });

      if (updateError) throw updateError;

      // Sign out the user so they must log in with their new password
      await supabase.auth.signOut();

      setSuccess(true);
      showToast('Password updated successfully');

      setTimeout(() => {
        // Clean URL and navigate to home
        window.history.replaceState(null, '', '/');
        navigateTo('home');
      }, 2000);
    } catch (error: any) {
      console.error('Password update error:', error);
      showToast(error.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestNewLink = () => {
    // Clean URL and navigate to auth with reset mode via restoration state
    window.history.replaceState(null, '', '/');
    navigateTo('auth', undefined, { authMode: 'reset' });
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center px-6">
        <div className="max-w-md w-full">
          <div className="bg-gradient-to-br from-[#181818] to-[#1a1a1a] rounded-3xl border border-[#FF6B6B]/20 shadow-2xl shadow-black/50 p-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#FF6B6B]/10 flex items-center justify-center mx-auto mb-6">
                <div className="text-[#FF6B6B] text-3xl">⚠</div>
              </div>

              <h2 className="text-[24px] font-semibold text-[#F5F5F5] mb-3">
                Link expired or already used
              </h2>

              <p className="text-[15px] text-[#C5C5C5] leading-relaxed mb-8">
                For security, password reset links can only be used once and expire after a limited time. Request a new link to continue.
              </p>

              <Button
                onClick={handleRequestNewLink}
                variant="primary"
                fullWidth
              >
                Request a new reset link
              </Button>

              <button
                onClick={() => {
                  // Clean URL and navigate to home
                  window.history.replaceState(null, '', '/');
                  navigateTo('home');
                }}
                className="w-full text-[14px] text-[#C5C5C5] hover:text-[#F5F5F5] transition-colors py-3 mt-3"
              >
                Return to app
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center px-6">
        <div className="max-w-md w-full">
          <div className="bg-gradient-to-br from-[#181818] to-[#1a1a1a] rounded-3xl border border-[#40E0D0]/20 shadow-2xl shadow-black/50 p-8">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-[#40E0D0]/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-[#40E0D0]" />
              </div>

              <h2 className="text-[26px] font-semibold text-[#F5F5F5] mb-3">
                Password Updated
              </h2>

              <p className="text-[15px] text-[#C5C5C5] leading-relaxed mb-6">
                Your password has been updated successfully.
              </p>

              <p className="text-[13px] text-[#A0A0A0]">
                Returning to app...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading while establishing session
  if (!sessionReady && !error) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center px-6">
        <div className="max-w-md w-full">
          <div className="bg-gradient-to-br from-[#181818] to-[#1a1a1a] rounded-3xl border border-[#40E0D0]/20 shadow-2xl shadow-black/50 p-8">
            <div className="text-center py-8">
              <div className="w-16 h-16 border-4 border-[#40E0D0]/20 border-t-[#40E0D0] rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-[15px] text-[#C5C5C5]">Verifying reset link...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111111] flex items-center justify-center px-6">
      <div className="max-w-md w-full">
        <div className="bg-gradient-to-br from-[#181818] to-[#1a1a1a] rounded-3xl border border-[#40E0D0]/20 shadow-2xl shadow-black/50 p-8">
          <div className="mb-6">
            <h2 className="text-[26px] font-semibold text-[#F5F5F5] mb-3 leading-tight">
              Create New Password
            </h2>
            <p className="text-[15px] text-[#C5C5C5] leading-relaxed">
              Enter a new password for your account.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="password" className="block text-[13px] font-medium text-[#B0B0B0] mb-2">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="w-full px-4 py-3 bg-[#0f0f0f] border border-[#4a4a4a] rounded-xl text-[15px] text-[#F5F5F5] placeholder-[#888888] focus:outline-none focus:border-[#40E0D0] focus:bg-[#141414] transition-all pr-12 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0A0A0] hover:text-[#F5F5F5] transition-colors"
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-[13px] font-medium text-[#B0B0B0] mb-2">
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    // Clear error when user starts typing to fix the issue
                    if (showMatchError) {
                      setShowMatchError(false);
                    }
                  }}
                  onBlur={() => {
                    // Only show error on blur if both fields have values and don't match
                    if (confirmPassword && password && password !== confirmPassword) {
                      setShowMatchError(true);
                    }
                  }}
                  onPaste={(e) => e.preventDefault()}
                  placeholder="Re-enter your password"
                  className="w-full px-4 py-3 bg-[#0f0f0f] border border-[#4a4a4a] rounded-xl text-[15px] text-[#F5F5F5] placeholder-[#888888] focus:outline-none focus:border-[#40E0D0] focus:bg-[#141414] transition-all pr-12 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0A0A0] hover:text-[#F5F5F5] transition-colors"
                  disabled={loading}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {showMatchError && password !== confirmPassword && (
              <p className="text-[13px] text-[#FF6B6B] mt-2">
                Passwords do not match
              </p>
            )}

            <Button
              type="submit"
              disabled={loading || !password || !confirmPassword || password !== confirmPassword}
              variant="primary"
              fullWidth
              className="mt-7"
            >
              {loading ? 'Updating Password...' : 'Update Password'}
            </Button>
          </form>

          <div className="mt-6">
            <button
              onClick={() => {
                // Clean URL and navigate to home
                window.history.replaceState(null, '', '/');
                navigateTo('home');
              }}
              disabled={loading}
              className="w-full text-[14px] text-[#C5C5C5] hover:text-[#F5F5F5] transition-colors py-3"
            >
              Return to app
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
